/**
 * Creates a cancellable API request using the AbortController.
 *
 * This function accepts a callback that is passed an `AbortSignal`, allowing
 * the callback to make cancellable API requests. You need to pass this signal
 * to the fetch function as a `signal` option.
 *
 * Returns cleanup function that aborts the API request when invoked. Yoj can
 * use it for cleanup when the component is unmounted.
 *
 * Example:
 * ```
 * useEffect(() => {
 *   return disposeApi(async (signal) => {
 *     const { signedUrl } = await getFileSignedUrl(doc.id, key, { signal });
 *     setSrc(signedUrl);
 *   });
 * }, [key, src, doc.id]);
 * ```
 */
export const disposeApi = (
  /**
   * The async callback function that receives the `AbortSignal` and handles the API
   * request. The API request can listen for the signal to cancel the request when
   * the component is unmounted or on other cancellation conditions.
   */
  callback: (signal: AbortSignal) => Promise<void>,
  unmountCallback?: () => void,
): (() => void) => {
  const abortController = new AbortController();
  callback(abortController.signal);
  return () => {
    abortController.abort("Component unmount");
    unmountCallback?.();
  };
};
